import { Injectable, signal } from '@angular/core';

export interface ExtractedBrd {
  title: string;
  version: string;
  content: string;
}

export type ExtractionProgress =
  | { stage: 'idle' }
  | { stage: 'loading-pdf' }
  | { stage: 'rendering'; page: number; total: number }
  | { stage: 'recognizing'; page: number; total: number; percent: number }
  | { stage: 'formatting' };

@Injectable({ providedIn: 'root' })
export class PdfExtractorService {
  readonly progress = signal<ExtractionProgress>({ stage: 'idle' });

  async extractBrd(file: File): Promise<ExtractedBrd> {
    this.progress.set({ stage: 'loading-pdf' });

    const [{ getDocument, GlobalWorkerOptions }, { createWorker }] = await Promise.all([
      import('pdfjs-dist'),
      import('tesseract.js'),
    ]);
    GlobalWorkerOptions.workerSrc = new URL(
      'pdfjs-dist/build/pdf.worker.mjs',
      import.meta.url,
    ).toString();

    const buffer = await file.arrayBuffer();
    const pdf = await getDocument({ data: buffer }).promise;
    const total = pdf.numPages;

    const worker = await createWorker(['ara', 'eng'], 1, {
      logger: (m) => {
        if (m.status === 'recognizing text') {
          const current = this.progress();
          const page = current.stage === 'recognizing' ? current.page : 1;
          this.progress.set({
            stage: 'recognizing',
            page,
            total,
            percent: Math.round(m.progress * 100),
          });
        }
      },
    });

    try {
      const pageTexts: string[] = [];
      for (let i = 1; i <= total; i++) {
        this.progress.set({ stage: 'rendering', page: i, total });
        const canvas = await this.renderPageToCanvas(pdf, i);
        this.progress.set({ stage: 'recognizing', page: i, total, percent: 0 });
        const { data } = await worker.recognize(canvas);
        pageTexts.push(data.text.trim());
      }

      this.progress.set({ stage: 'formatting' });
      const formatted = this.postFormat(pageTexts.join('\n\n'));
      return {
        title: this.extractTitle(formatted),
        version: this.extractVersion(formatted),
        content: formatted,
      };
    } finally {
      await worker.terminate();
      this.progress.set({ stage: 'idle' });
    }
  }

  private async renderPageToCanvas(pdf: any, pageNum: number): Promise<HTMLCanvasElement> {
    const page = await pdf.getPage(pageNum);
    const viewport = page.getViewport({ scale: 2.2 });
    const canvas = document.createElement('canvas');
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) throw new Error('Could not get canvas context');
    await page.render({ canvasContext: ctx, viewport, canvas }).promise;
    return canvas;
  }

  private postFormat(text: string): string {
    const lines = text.split('\n').map((l) => l.trimEnd());
    const normalized: string[] = [];

    const bulletChars = '[•●○▪◦▸►➢➣➤*]';
    const ocrBulletChars = '[eo®©°]';

    for (let i = 0; i < lines.length; i++) {
      let line = lines[i];

      if (new RegExp(`^${bulletChars}$`).test(line.trim()) && lines[i + 1]) {
        line = '- ' + lines[++i];
      }

      line = line.replace(
        new RegExp(
          `^\\s*(?:[-–—]|${bulletChars}|${ocrBulletChars}(?=\\s+[A-Z\\u0590-\\u08FF]))\\s+`,
        ),
        '- ',
      );

      if (/^\s*\d+(?:\.\d+)*\.?\s*$/.test(line) && lines[i + 1] && /^\s*\S/.test(lines[i + 1])) {
        line = `## ${line.trim()} ${lines[++i].trim()}`;
      } else {
        const numbered = line.match(/^\s*(\d+(?:\.\d+)*\.)\s+(.+)$/);
        if (numbered && numbered[2].length < 100 && /^[A-Z\u0590-\u08FF]/.test(numbered[2])) {
          line = `## ${numbered[1]} ${numbered[2]}`;
        }
      }

      normalized.push(line);
    }

    return normalized
      .join('\n')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  }

  private extractTitle(text: string): string {
    const firstLine = text.split('\n').find((l) => l.trim().length > 3) ?? '';
    return firstLine.replace(/^#+\s*/, '').trim().slice(0, 120);
  }

  private extractVersion(text: string): string {
    const patterns = [
      /\bversion\s*[:\-]?\s*(\d+\.\d+(?:\.\d+)?)\b/i,
      /\bv(\d+\.\d+(?:\.\d+)?)\b/i,
      /\brev(?:ision)?\s*[:\-]?\s*(\d+\.\d+(?:\.\d+)?)\b/i,
    ];
    for (const p of patterns) {
      const m = text.match(p);
      if (m) return m[1];
    }
    return '';
  }
}
